<script setup lang="ts">
import { Alert, type IAlertInfoItem } from "@frontiers-vue3/public-pages";
import { isoToDateConverter, replaceDynamicVars } from "~/helpers";
import type { IRTOverviewSubmissionBox } from "./IRTOverviewSubmissionBox";

const props = defineProps<IRTOverviewSubmissionBox>();
const { isCurrentResearchTopicOpen } = useMain();
const config = useRuntimeConfig();
const terminology = useTerminologyStore();
const currentDate = new Date(new Date().toISOString());

const isSubmissionDeadlinesVisible = String(config.public?.isSubmissionDeadlinesVisible).toLowerCase() === "true";
const hideManuscriptSubmissionDeadline = String(config.public?.hideManuscriptSubmissionDeadline).toLowerCase() === "true";
const showAcceptingArticlesText = String(config.public?.showAcceptingArticlesText).toLowerCase() === "true";
const computedInfoItems = computed((): IAlertInfoItem[] => {
  const deadlineContent = [];
  const infoItems: IAlertInfoItem[] = [];

  if (currentDate <= props.manuscriptSubmissionDeadline) {
    deadlineContent.push(
      `Manuscript Submission Deadline ${buildMonthString(
        props.manuscriptSubmissionDeadline
      )}`
    );
  }

  if (isManuscriptExtensionDeadlineVisible.value) {
    deadlineContent.push(
      `Manuscript Extension Submission Deadline ${buildMonthString(
        props.manuscriptSubmissionExtendedDeadline
      )}`
    );
  }

  if (deadlineContent.length)
    infoItems.push({ text: deadlineContent.join(" | "), links: [] });

  if (
    infoItems.length &&
    isCurrentResearchTopicOpen &&
    getSubmissionDeadlineText.value
  ) {
    infoItems.push({ text: getSubmissionDeadlineText.value, links: [] });
  }

  if (isCurrentResearchTopicOpen) {
    const guidelinesLink = {
      text: `Check ${terminology.getTerm("author").toLowerCase()} guidelines`,
      url: getAuthorGuidelinesLink()
    };

    if (infoItems.length) {
      infoItems[infoItems.length - 1].links = [guidelinesLink];
    } else {
      infoItems.push({ text: "", links: [guidelinesLink] });
    }
  }

  return infoItems;
});

const getSubmissionDeadlineText = computed((): string => {
  if (!showAcceptingArticlesText) return "";

  if (isCurrentResearchTopicOpen) {
    if (currentDate <= props.manuscriptSubmissionDeadline) {
      return "This Research Topic is currently accepting articles";
    } else if (currentDate > props.manuscriptSubmissionExtendedDeadline) {
      return "This Research Topic is currently accepting articles, but is closing soon.";
    }  else if (currentDate > props.manuscriptSubmissionDeadline && hideManuscriptSubmissionDeadline) {
      return "This Research Topic is currently accepting articles, but is closing soon.";
    }
  }

  return "";
});

const isManuscriptExtensionDeadlineVisible = computed(() => {
  const minDate = new Date("01/01/0001");

  let hideManuscriptSubmissionFromDate = "";
  if (config.public.hideManuscriptSubmissionFromDate) {
    const [sDate, sMonth, sYear] =
      config.public.hideManuscriptSubmissionFromDate.split("/");
    hideManuscriptSubmissionFromDate = `${sMonth}/${sDate}/${sYear}`;
  }

  const createdDate = props.createdDate ? props.createdDate : minDate;

  return ((
    createdDate < new Date(hideManuscriptSubmissionFromDate) &&
    currentDate <= props.manuscriptSubmissionExtendedDeadline
  ) || (
    currentDate > props.manuscriptSubmissionDeadline && 
    currentDate <= props.manuscriptSubmissionExtendedDeadline &&
    !hideManuscriptSubmissionDeadline
  ));
});

const hasAnyInfoText = computed(() => {
  return computedInfoItems.value.some((item) =>
    item && item.text && item.text.trim().length > 0
  );
});

const submissionBoxMessage = computed((): string => {
  if (!isCurrentResearchTopicOpen) {
    return "This Research Topic is closed for submissions.";
  } else if (hasAnyInfoText.value) {
    return "Submission deadlines";
  } else {
    return getSubmissionDeadlineText.value;
  }
});

const getAuthorGuidelinesLink = (): string => {
  const chGuidelinesLink =
    config.public.researchTopicPageAuthorGuidelinesLink || "";
  const primaryJournalSlug = useMain().getPrimaryJournal?.slug || "";

  return replaceDynamicVars(chGuidelinesLink, {
    primaryJournalSlug
  });
};

const buildMonthString = (date: Date) => {
  return isoToDateConverter(date, "long");
};
</script>

<template>
  <Alert
    v-if="isSubmissionDeadlinesVisible"
    :type="'info'"
    :message="submissionBoxMessage"
    :show-close="false"
    :info-items="computedInfoItems">
  </Alert>
</template>

<style lang="scss">
.Alert {
  padding: 16px;
  padding-left: 24px;

  @media #{$break-md} {
    &::before {
      height: calc(100% + 2px);
    }
  }

  &__infoItem {
    p.Alert__infoItem__text {
      @include f-16-300;
    }

    & + &::before {
      border: 0 !important;
    }

    .Link__wrapper {
      .Link {
        @include f-16-500;
        &::after {
          line-height: 15px;
          color: $grey80;
        }
        &:hover {
          &::after {
            color: var(--mainColor70);
          }
        }
      }
    }
  }

  &__icon {
    svg {
      width: 16px;
    }
  }
}
</style>
